import { z } from 'zod'
import { FirestoreTimestamp } from './utils'

// Eligibility criteria schema for maintenance items
export const MaintenanceItemCriteria = z.object({
  acopumpType: z.array(z.string()).default([]),
  brakeTypes: z.array(z.string()).default([]),
  bufferTypes: z.array(z.string()).default([]),
  doorOperatorTypes: z.array(z.string()).default([]),
  doorPanelTypes: z.array(z.string()).default([]),
  doorRestrictorTypes: z.array(z.string()).default([]),
  driveTypes: z.array(z.string()).default([]),
  elevatorTypes: z.array(z.string()).default([]),
  governorTypes: z.array(z.string()).default([]),
  guideTypes: z.array(z.string()).default([]),
  hasHoistwayLock: z.array(z.boolean()).default([]), // Not used in Surveys
  hoistwayDoorTypes: z.array(z.string()).default([]),
  serviceTypes: z.array(z.string()).default([]),
  suspensionMeansTypes: z.array(z.string()).default([]),
  travelingCableTypes: z.array(z.string()).default([]),

  // Equipment presence requirements (boolean arrays)
  hasAntiSlideDevices: z.array(z.boolean()).default([]),
  hasBatteryLowering: z.array(z.boolean()).default([]),
  hasBrokenRopeSelectorSwitch: z.array(z.boolean()).default([]),
  hasBrokenSuspensionDetection: z.array(z.boolean()).default([]),
  hasBufferSwitches: z.array(z.boolean()).default([]),
  hasCarSafeties: z.array(z.boolean()).default([]),
  hasCeilingGuards: z.array(z.boolean()).default([]),
  hasCombImpactDetection: z.array(z.boolean()).default([]),
  hasCompensation: z.array(z.boolean()).default([]),
  hasCompensationSheave: z.array(z.boolean()).default([]),
  hasCounterWeights: z.array(z.boolean()).default([]),
  hasCounterweightSafeties: z.array(z.boolean()).default([]),
  hasCylinderCorrosionProtection: z.array(z.boolean()).default([]),
  hasDriveChainDeviceSwitch: z.array(z.boolean()).default([]),
  hasEmergencyPower: z.array(z.boolean()).default([]),
  hasFirefighterService: z.array(z.boolean()).default([]),
  hasFlexibleHose: z.array(z.boolean()).default([]),
  hasGateSwitch: z.array(z.boolean()).default([]),
  hasHandrailSpeedMonitoring: z.array(z.boolean()).default([]),
  hasHoistwayAccess: z.array(z.boolean()).default([]),
  hasLevelPalletDevice: z.array(z.boolean()).default([]),
  hasMGSet: z.array(z.boolean()).default([]),
  hasMissingPalletDevice: z.array(z.boolean()).default([]),
  hasNonMetallicSheaveLiner: z.array(z.boolean()).default([]),
  hasPlungerGripper: z.array(z.boolean()).default([]),
  hasPressureSwitch: z.array(z.boolean()).default([]),
  hasPressureVessel: z.array(z.boolean()).default([]),
  hasSeismicDevices: z.array(z.boolean()).default([]),
  hasTractionLossDetection: z.array(z.boolean()).default([]),
  hasUnderPlatformObstructionDevice: z.array(z.boolean()).default([]),
  hasVisionPanels: z.array(z.boolean()).default([]),
  isMachineRoomLess: z.array(z.boolean()).default([]), // Machine Room Less
  isObservationElevator: z.array(z.boolean()).default([]),
})
export type MaintenanceItemCriteria = z.infer<typeof MaintenanceItemCriteria>

// Task definition schema
export const MaintenanceItemDefinition = z.object({
  // Core task info
  task: z.string(),
  description: z.string(),
  codeRef: z.string().nullable(),

  // Scheduling
  minInterval: z.string().optional(),
  maxInterval: z.string().optional(),

  // Time estimates
  minutes: z.string().optional(),
  secondManMinutes: z.string().optional(),
  secondManRepairMinutes: z.string().optional(),
  secondManTestMinutes: z.string().optional(),
  testMinutes: z.string().optional(),

  // Dependencies and notes
  dependency: z.string().optional(),
  notes: z.string().optional(),
  mustBePerformedTitle: z.string().optional(),
  repairReplacement: z.string().optional(),

  // Display and organization
  headingOrder: z.string().optional(),
  headingTitle: z.string().optional(),
  isHeader: z.string().optional(),

  // Task type classification
  maintOrTest: z.string().optional(),
  maintenanceTasksTitle: z.string().optional(),
  testTasksYearTitle: z.string().optional(),

  // Personnel requirements
  twoManIndicator: z.string().optional(),

  // Technical details
  typeOfItem: z.string().optional(),

  // Misc fields
  lockedWith: z.string().optional(),
})
export type MaintenanceItemDefinition = z.infer<
  typeof MaintenanceItemDefinition
>

// Main maintenance item definition combining both
export const MaintenanceItemDef = z.object({
  // Unique identifier
  itemId: z.string(),

  // Task definition
  definition: MaintenanceItemDefinition,

  // Eligibility criteria
  criteria: MaintenanceItemCriteria,

  // Migration and audit fields
  createdAt: FirestoreTimestamp.optional(),
  updatedAt: FirestoreTimestamp.optional(),
  migratedAt: FirestoreTimestamp.optional(),
})
export type MaintenanceItemDef = z.infer<typeof MaintenanceItemDef>
